import clsx from "clsx"
import {
	ReactNode,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react"

export interface DropDownProps {
	children: ReactNode
	trigger: ReactNode
	className?: string
	contentClassName?: string
	onFocus?: () => void
	hover?: boolean
	closeOnClick?: boolean
	isOpen?: boolean
	zIndex?: string
	triggerClassName?: string
	closeInSelect?: boolean
	mobileBackground?: boolean
}

export function DropDown(props: DropDownProps) {
	const {
		trigger,
		children,
		className,
		contentClassName,
		onFocus,
		hover,
		closeOnClick,
		isOpen,
		zIndex,
		triggerClassName,
		closeInSelect,
		mobileBackground,
	} = props

	const buttonRef = useRef<HTMLButtonElement>(null)

	const handleClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.stopPropagation()
		onFocus && onFocus()

		// Thanks Safari
		buttonRef.current?.focus()
	}

	return (
		<>
			<div
				className={clsx(
					zIndex ? zIndex : "z-[66px]",
					className,
					"relative",
				)}>
				<button
					ref={buttonRef}
					type="button"
					onClick={handleClick}
					className={clsx(
						"peer focus:border-none focus:outline-none",
						triggerClassName,
					)}>
					{trigger}
				</button>
				{mobileBackground && (
					<div
						className={clsx(
							isOpen
								? "pointer-events-auto opacity-100"
								: "pointer-events-none opacity-0",
							"fixed z-10 h-screen w-screen bg-color-popup tablet:hidden",
							"left-0 top-[58px] opacity-0 peer-focus:opacity-100",
						)}
					/>
				)}
				<div
					className={clsx(
						!closeOnClick &&
							"focus-within:pointer-events-auto focus-within:opacity-100",
						"hover:pointer-events-auto",
						!closeInSelect && "hover:opacity-100",
						hover
							? "peer-hover:pointer-events-auto peer-hover:opacity-100"
							: "peer-focus:pointer-events-auto peer-focus:opacity-100",
						"z-[66] transition-all",
						isOpen
							? "pointer-events-auto opacity-100"
							: "pointer-events-none opacity-0",
						contentClassName,
					)}>
					{children}
				</div>
			</div>
		</>
	)
}

export function DropDownAlternative(props: DropDownProps) {
	const {
		trigger,
		children,
		className,
		contentClassName,
		onFocus,
		hover,
		closeOnClick,
	} = props

	return (
		<>
			<div className={clsx("relative", className)}>
				<button
					type="button"
					onClick={(event) => {
						onFocus && onFocus()
						// thanks Safari
						event.target instanceof HTMLElement &&
							event.target.focus()
					}}
					className="peer z-[66]">
					{trigger}
				</button>
				<div
					className={clsx(
						!closeOnClick &&
							"focus-within:pointer-events-auto focus-within:opacity-100",
						"hover:pointer-events-auto hover:opacity-100",
						hover
							? "peer-hover:pointer-events-auto peer-hover:opacity-100"
							: "peer-focus:pointer-events-auto peer-focus:opacity-100",
						"pointer-events-none opacity-0",
						"z-[66] transition-all",
						contentClassName,
					)}>
					{children}
				</div>
			</div>
		</>
	)
}

interface DropDownWithStateProps {
	isOpen: boolean
	setIsOpen: (v: boolean) => void
	children: ReactNode
	trigger: ReactNode
	className?: string
	contentClassName?: string
	triggerClassName?: string
}
export function DropDownWithState(
	props: DropDownWithStateProps,
) {
	const {
		trigger,
		children,
		className,
		isOpen,
		triggerClassName,
		setIsOpen,
	} = props

	const buttonRef = useRef<HTMLButtonElement>(null)

	const handleClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.stopPropagation()
		setIsOpen(!isOpen)

		// Thanks Safari
		buttonRef.current?.focus()
	}

	const dropdownRef = useRef<HTMLDivElement>(null)
	// useOutsideClick({
	// 	ref: dropdownRef,
	// 	handler: () => setIsOpen(false),
	// })

	return (
		<>
			<div
				ref={dropdownRef}
				tabIndex={-1}
				onBlur={(e) => {
					if (
						dropdownRef.current &&
						!dropdownRef.current.contains(
							e.relatedTarget as Node,
						)
					) {
						setIsOpen(false)
					}
				}}
				className={clsx("z-[66px]", className, "relative")}>
				<button
					ref={buttonRef}
					type="button"
					onClick={handleClick}
					className={clsx(
						"focus:border-none focus:outline-none",
						triggerClassName,
					)}>
					{trigger}
				</button>
				{children}
			</div>
		</>
	)
}

export enum DropDownPosition {
	TOP = "top",
	BOTTOM = "bottom",
	LEFT = "left",
	RIGHT = "right",
	TOP_LEFT = "top-left",
	TOP_RIGHT = "top-right",
	BOTTOM_LEFT = "bottom-left",
	BOTTOM_RIGHT = "bottom-right",
}

interface DropDownContentProps {
	isOpen: boolean
	className?: string
	position?: DropDownPosition
	children: ReactNode
	parentRef?: RefObject<HTMLElement>
}

export function DropdownContent(
	props: DropDownContentProps,
) {
	const {
		isOpen,
		className,
		children,
		position = DropDownPosition.BOTTOM,
		parentRef,
	} = props

	const [calculatedPosition, setCalculatedPosition] =
		useState<DropDownPosition>(position)

	const dropdownRef = useRef<HTMLDivElement>(null)

	const recalculatePosition = useCallback(() => {
		if (!dropdownRef.current || !parentRef?.current) return

		const dropdownRect =
			dropdownRef.current.getBoundingClientRect()
		const parentRect =
			parentRef.current.getBoundingClientRect()

		let newPosition = calculatedPosition

		if (
			calculatedPosition.includes("bottom") &&
			dropdownRect.bottom > parentRect.bottom
		) {
			newPosition = calculatedPosition.replace(
				"bottom",
				"top",
			) as DropDownPosition
		} else if (
			calculatedPosition.includes("top") &&
			dropdownRect.top < parentRect.top
		) {
			newPosition = calculatedPosition.replace(
				"top",
				"bottom",
			) as DropDownPosition
		}

		if (
			newPosition.includes("right") &&
			dropdownRect.left < parentRect.left
		) {
			newPosition = newPosition.replace(
				"right",
				"left",
			) as DropDownPosition
		} else if (
			newPosition.includes("left") &&
			dropdownRect.right > parentRect.right
		) {
			newPosition = newPosition.replace(
				"left",
				"right",
			) as DropDownPosition
		}

		if (newPosition !== calculatedPosition) {
			setCalculatedPosition(newPosition)
		}
	}, [calculatedPosition, parentRef])

	useEffect(() => {
		if (!isOpen || !parentRef) return

		recalculatePosition()

		const handleWindowChange = () => {
			recalculatePosition()
		}

		window.addEventListener("scroll", handleWindowChange)
		window.addEventListener("resize", handleWindowChange)

		return () => {
			window.removeEventListener(
				"scroll",
				handleWindowChange,
			)
			window.removeEventListener(
				"resize",
				handleWindowChange,
			)
		}
	}, [isOpen, parentRef, recalculatePosition])

	const positionClasses = {
		[DropDownPosition.TOP]: "bottom-full",
		[DropDownPosition.BOTTOM]: "top-full",
		[DropDownPosition.LEFT]: "right-full",
		[DropDownPosition.RIGHT]: "left-full",
		[DropDownPosition.TOP_LEFT]: "bottom-full left-0",
		[DropDownPosition.TOP_RIGHT]: "bottom-full right-0",
		[DropDownPosition.BOTTOM_LEFT]: "top-full left-0",
		[DropDownPosition.BOTTOM_RIGHT]: "top-full right-0",
	}

	return (
		<div
			ref={dropdownRef}
			className={clsx(
				"absolute z-[66] transition-all",
				positionClasses[calculatedPosition],
				isOpen
					? "pointer-events-auto opacity-100"
					: "pointer-events-none opacity-0",
				className,
			)}>
			{children}
		</div>
	)
}
